<script setup lang="ts">
import {nextTick, onBeforeUnmount, ref, watch} from 'vue';

// A sheet expanded over the whole page so an engineer can work it with room,
// then minimized back to where it came from. The slot renders in the same
// place either way and only the wrapper moves, so what it holds (a filter,
// a scroll position, a pending edit) survives opening and closing.
const props = withDefaults(defineProps<{
  open: boolean;
  label: string;
  // Where focus goes back on close. Safari does not focus a clicked button,
  // so the opener hands over the control it used.
  returnFocus?: HTMLElement | null;
}>(), {returnFocus: null});

const emit = defineEmits<{
  close: [];
}>();

const root = ref<HTMLElement | null>(null);
const closeButton = ref<HTMLButtonElement | null>(null);

// aria-modal alone keeps neither Tab nor the screen reader inside, so the rest
// of the page is inert while the sheet is open.
let inertElements: HTMLElement[] = [];

function makeRestInert() {
  inertElements = Array.from(document.body.children).filter(
    (element): element is HTMLElement => element instanceof HTMLElement && element !== root.value && !element.inert,
  );
  for (const element of inertElements) element.inert = true;
}

// On window, where Escape arrives even when focus has fallen to the page. A
// control inside that owns Escape (a cell being edited) stops it first.
function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') emit('close');
}

// Bumped by every open and every release, so the half of opening that waits
// for the render can tell the sheet closed, unmounted or reopened meanwhile
// and must not make a page with no sheet on it inert.
let generation = 0;

function release() {
  generation++;
  window.removeEventListener('keydown', onKeydown);
  document.body.classList.remove('drcy-sheet-workspace-open');
  for (const element of inertElements) element.inert = false;
  inertElements = [];
}

watch(() => props.open, async (open, wasOpen) => {
  if (open) {
    const opened = ++generation;
    window.addEventListener('keydown', onKeydown);
    document.body.classList.add('drcy-sheet-workspace-open');
    // After the render, which moves the sheet under the body.
    await nextTick();
    if (opened !== generation) return;
    makeRestInert();
    closeButton.value?.focus();
  } else if (wasOpen) {
    release();
    // After the render: the opener may hide its control while the workspace
    // is open, and a hidden element cannot take focus. No scroll: the page
    // under the workspace never moved, and the opener may be scrolling it.
    await nextTick();
    props.returnFocus?.focus({preventScroll: true});
  }
}, {immediate: true});

onBeforeUnmount(() => {
  if (!props.open) return;
  release();
  props.returnFocus?.focus({preventScroll: true});
});
</script>

<template>
  <Teleport to="body" :disabled="!open">
    <div
      ref="root"
      :class="open ? 'drcy-sheet-workspace' : 'drcy-sheet-inline'"
      :role="open ? 'dialog' : undefined"
      :aria-modal="open ? 'true' : undefined"
      :aria-label="open ? label : undefined"
    >
      <header v-if="open" class="drcy-sheet-workspace-header">
        <slot name="header"/>
        <button ref="closeButton" class="ui small basic button drcy-sheet-workspace-close" type="button" @click="emit('close')">Minimize</button>
      </header>
      <slot/>
    </div>
  </Teleport>
</template>

<style scoped>
.drcy-sheet-workspace {
  /* The sheet fills the screen; no cap here. */
  --drcy-csv-max-height: none;
  --drcy-component-table-max-height: none;
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem 1.25rem 1.25rem;
  background: var(--color-body);
}
.drcy-sheet-inline {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.drcy-sheet-workspace-header {
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 0.75rem;
}
</style>

<style> /* eslint-disable-line vue-scoped-css/enforce-style-type */
/* The page under an open workspace does not scroll. */
body.drcy-sheet-workspace-open {
  overflow: hidden;
}
/* Unscoped so the opener's own header buttons, passed in the slot, line up
   with Minimize on the header's gap rather than Fomantic's button margin. */
.drcy-sheet-workspace-header > .button {
  margin: 0 !important;
}
</style>
